import React, { useRef, useState } from 'react';
import { ResourceManager } from './components/ResourceManager';
import { useSupabaseResource } from '@/hooks/useSupabaseResource';
import { supabase } from '@/lib/supabaseClient';
import { Download, Upload } from 'lucide-react';
import SingleImageUpload from '@/components/SingleImageUpload';

const ListingTeamModule = () => {
  const { records, refresh } = useSupabaseResource('listing_team');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const exportToCSV = () => {
    const headers = ['name', 'title', 'email', 'phone', 'display_order', 'is_active'];
    const csvContent = [
      headers.join(','),
      ...records.map((record) =>
        headers
          .map((h) => `"${record[h] ?? ''}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'listing_team.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter((line) => line.trim());
      if (lines.length < 2) {
        alert('CSV must have headers and at least one row');
        return;
      }

      const headers = lines[0].split(',').map((h) => h.replace(/"/g, '').trim());
      const requiredHeaders = ['name', 'email'];
      const missing = requiredHeaders.filter((h) => !headers.includes(h));
      if (missing.length) {
        alert(`Missing required headers: ${missing.join(', ')}`);
        return;
      }

      const entries = lines.slice(1).map((line) => {
        const values = line.split(',').map((v) => v.replace(/"/g, '').trim());
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i] || '';
        });
        obj.display_order = obj.display_order ? Number(obj.display_order) : 0;
        obj.is_active = obj.is_active !== 'false';
        return obj;
      });

      const { error } = await supabase.from('listing_team').insert(entries);
      if (error) throw error;
      alert('Bulk upload successful');
      refresh();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const formFields = [
    { name: 'name', label: 'Name', placeholder: 'Agent Name' },
    { name: 'title', label: 'Title/Role', placeholder: 'Lead Agent' },
    {
      name: 'avatar_url',
      label: 'Avatar / Photo',
      type: 'custom',
      component: SingleImageUpload,
      props: { label: 'Upload avatar', placeholder: 'https://example.com/photo.jpg' },
    },
    { name: 'email', label: 'Email', type: 'email', placeholder: 'agent@example.com' },
    { name: 'phone', label: 'Phone', placeholder: '+256 700 000000' },
    { name: 'display_order', label: 'Display Order', type: 'number', placeholder: '0' },
    { name: 'is_active', label: 'Active', type: 'checkbox' },
  ];

  const columns = [
    { label: 'Name', accessor: 'name' },
    { label: 'Title', accessor: 'title' },
    {
      label: 'Photo',
      accessor: 'avatar_url',
      render: (value) =>
        value ? <img src={value} alt="avatar" className="h-10 w-10 rounded-full object-cover" /> : '—',
    },
    { label: 'Email', accessor: 'email' },
    { label: 'Phone', accessor: 'phone' },
    { label: 'Order', accessor: 'display_order' },
    {
      label: 'Active',
      accessor: 'is_active',
      render: (value) => (value ? 'Yes' : 'No'),
    },
  ];

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />
      <ResourceManager
        table="listing_team"
        title="Listing Team"
        description="Manage agents or listers responsible for properties."
        columns={columns}
        formFields={formFields}
        defaultValues={{
          name: '',
          title: '',
          avatar_url: '',
          email: '',
          phone: '',
          display_order: 0,
          is_active: true,
        }}
        transformBeforeSave={(payload) => ({
          ...payload,
          display_order: payload.display_order ? Number(payload.display_order) : 0,
          is_active: Boolean(payload.is_active),
        })}
        onEdit={(record) => ({
          ...record,
        })}
        extraButtons={[
          { label: 'Export CSV', onClick: exportToCSV, icon: Download },
          {
            label: uploading ? 'Uploading...' : 'Bulk Upload',
            onClick: () => fileInputRef.current.click(),
            disabled: uploading,
            icon: Upload,
          },
        ]}
      />
    </>
  );
};

export default ListingTeamModule;
